import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { RefreshCw, Database, MessageSquare, Mail, HardDrive, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

/**
 * Operational health dashboard for admins. Read-only aggregate of:
 *  - DB ping latency and per-collection row counts
 *  - Twilio configuration + last 5 outbound message statuses
 *  - Resend configuration
 *  - Storage backend
 */
export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system/health");
      setData(data);
      setLastFetch(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load system health");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div data-testid="system-health-page">
      <PageHeader
        title="System Health"
        subtitle="Operational diagnostics"
        actions={
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="system-health-refresh"
            className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-gray-300 hover:bg-gray-900 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading" : "Refresh"}
          </button>
        }
      />

      <div className="p-8 space-y-8">
        {!data ? (
          <div className="text-sm text-gray-500 font-mono">Loading…</div>
        ) : (
          <>
            <IntegrationsRow data={data} />
            <TwilioMessages data={data.twilio?.recent} />
            <CollectionsGrid rows={data.collections} />
          </>
        )}

        {lastFetch && (
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-mono">
            Snapshot @ {lastFetch.toLocaleTimeString()} · Server: {data?.server_time?.slice(11, 19)} UTC
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ ok, label }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${
      ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"
    }`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {label}
    </span>
  );
}

function Card({ icon: Icon, title, status, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-700" strokeWidth={2} />
          <div className="font-display font-bold text-sm tracking-tight uppercase">{title}</div>
        </div>
        {status}
      </div>
      <div className="text-xs font-mono text-gray-700 space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-gray-500 uppercase text-[10px] tracking-widest">{k}</span>
      <span className="text-gray-900 truncate">{v ?? "—"}</span>
    </div>
  );
}

function IntegrationsRow({ data }) {
  const db = data.database || {};
  const t = data.twilio || {};
  const r = data.resend || {};
  const s = data.storage || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="system-health-integrations">
      <Card
        icon={Database}
        title="MongoDB"
        status={<StatusBadge ok={db.ok} label={db.ok ? "Healthy" : "Down"} />}
        testid="health-card-mongo"
      >
        <KV k="Latency" v={db.latency_ms != null ? `${db.latency_ms} ms` : "—"} />
        {db.error && <div className="text-red-700">{db.error}</div>}
      </Card>

      <Card
        icon={MessageSquare}
        title="Twilio SMS"
        status={<StatusBadge ok={t.configured} label={t.configured ? "Configured" : "Missing"} />}
        testid="health-card-twilio"
      >
        <KV k="From" v={t.from_number} />
        <KV k="SID …" v={t.account_sid_tail} />
      </Card>

      <Card
        icon={Mail}
        title="Resend Email"
        status={<StatusBadge ok={r.configured} label={r.configured ? "Configured" : "Missing"} />}
        testid="health-card-resend"
      >
        <KV k="From" v={r.from_address} />
        {r.test_only_to && <KV k="Test-only to" v={r.test_only_to} />}
      </Card>

      <Card
        icon={HardDrive}
        title="Storage"
        status={<StatusBadge ok={s.configured} label="Ready" />}
        testid="health-card-storage"
      >
        <KV k="Backend" v={s.backend} />
        <KV k="App" v={s.app_name} />
      </Card>
    </div>
  );
}

function TwilioMessages({ data }) {
  if (!data) return null;
  if (!data.ok) {
    return (
      <div className="border border-yellow-300 bg-yellow-50 p-4 flex items-start gap-3" data-testid="twilio-recent-error">
        <AlertTriangle className="w-4 h-4 text-yellow-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-900">
          Twilio recent messages unavailable: <span className="font-mono">{data.error}</span>
        </div>
      </div>
    );
  }
  const messages = data.messages || [];
  const badge = (status) => {
    const map = {
      delivered: "bg-emerald-50 border-emerald-300 text-emerald-800",
      sent: "bg-blue-50 border-blue-300 text-blue-800",
      queued: "bg-gray-100 border-gray-300 text-gray-700",
      sending: "bg-gray-100 border-gray-300 text-gray-700",
      accepted: "bg-gray-100 border-gray-300 text-gray-700",
      undelivered: "bg-red-50 border-red-300 text-red-800",
      failed: "bg-red-50 border-red-300 text-red-800",
    };
    return map[status] || "bg-gray-100 border-gray-300 text-gray-700";
  };

  return (
    <div data-testid="twilio-recent-messages">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-gray-700" strokeWidth={2} />
        <h2 className="font-display font-bold text-sm tracking-tight uppercase">
          Recent Twilio SMS (last 5)
        </h2>
      </div>
      {messages.length === 0 ? (
        <div className="text-xs text-gray-500 font-mono px-1">No SMS activity yet.</div>
      ) : (
        <div className="border border-gray-200 bg-white overflow-hidden">
          <table className="w-full text-xs font-mono">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left uppercase tracking-widest text-[10px] text-gray-500">
                <th className="px-4 py-2">Sent</th>
                <th className="px-4 py-2">To</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Error</th>
                <th className="px-4 py-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.sid} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {(m.date_sent || m.date_created || "").replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{m.to}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block border px-2 py-0.5 uppercase text-[10px] tracking-widest ${badge(m.status)}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-red-700">
                    {m.error_code ? `${m.error_code}${m.error_message ? ": " + m.error_message : ""}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-700 truncate max-w-[400px]">{m.body_preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CollectionsGrid({ rows }) {
  return (
    <div data-testid="collections-grid">
      <div className="flex items-center gap-2 mb-3">
        <Database className="w-4 h-4 text-gray-700" strokeWidth={2} />
        <h2 className="font-display font-bold text-sm tracking-tight uppercase">Collection Row Counts</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {(rows || []).map((r) => (
          <div key={r.collection} className="border border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-gray-500">{r.collection}</span>
            <span className="font-display font-bold text-lg text-gray-900 tabular-nums">
              {r.count?.toLocaleString() ?? "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
